import React, { useState, ChangeEvent, FormEvent } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import testimage from '../assets/images/_.gif';

const API_BASE_URL = 'https://beat-fussion-backend.onrender.com/api';

type SignInType = 'viewer' | 'team';

interface SignInForm {
  email: string;
  password: string;
}

interface ApiErrorResponse {
  error?: string;
  message?: string;
  detail?: string;
}

// 🔔 Unified NotificationModal (success + error)
const NotificationModal: React.FC<{
  type: 'error' | 'success';
  message: string;
  onClose: () => void;
}> = ({ type, message, onClose }) => {
  const isError = type === 'error';
  const bg = isError ? 'bg-red-100' : 'bg-green-100';
  const border = isError ? 'border-red-400' : 'border-green-400';
  const text = isError ? 'text-red-700' : 'text-green-700';
  const title = isError ? 'Error' : 'Success';

  React.useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 flex items-start justify-center z-50">
      <div className="absolute inset-0 bg-black bg-opacity-25" onClick={onClose} />
      <div
        className={`mt-16 z-10 rounded-lg shadow-lg border ${border} ${bg} p-5 max-w-sm w-full animate-fadeIn`}
      >
        <h3 className={`font-semibold text-lg mb-1 ${text}`}>{title}</h3>
        <p className="text-gray-800 text-base">{message}</p>
      </div>
    </div>
  );
};

const SignIn: React.FC = () => {
  const [mode, setMode] = useState<SignInType>('viewer');
  const [form, setForm] = useState<SignInForm>({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'error' | 'success';
    message: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = mode === 'viewer' ? '/viewers/login/' : '/team/login/';
      const response = await axios.post(
        `${API_BASE_URL}${endpoint}`,
        {
          email: form.email,
          password: form.password,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const token = response.data?.access_token;
      const refresh = response.data?.refresh_token;
      const userData = response.data || {};

      // Proper name extraction
      const displayName =
        userData.first_name
          ? `${userData.first_name} ${userData.last_name || ''}`.trim()
          : userData.team_name || form.email.split('@')[0];

      // Save tokens
      if (token) localStorage.setItem('token', token);
      if (refresh) localStorage.setItem('refresh_token', refresh);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // ✅ Store in Auth Context
      login(
        {
          name: displayName,
          role: mode,
          token: token || '',
        },
        refresh
      );

      // ✅ Success notification + redirect
      setNotification({
        type: 'success',
        message:
          mode === 'viewer'
            ? 'Welcome back! Viewer sign-in successful.'
            : 'Welcome back! Team sign-in successful.',
      });

      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      let msg = 'An unexpected error occurred.';
      if (axios.isAxiosError<ApiErrorResponse>(err) && err.response) {
        const d = err.response.data;
        msg = d.error ?? d.message ?? d.detail ?? err.message;
      }
      setNotification({ type: 'error', message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Notification Modal */}
      {notification && (
        <NotificationModal
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="min-h-screen bg-white flex items-center justify-center p-4 sm:p-6 md:p-8">
        <div className="flex flex-col md:flex-row w-full max-w-5xl bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          
          {/* LEFT: Sign-in Form */}
          <div className="w-full md:w-1/2 flex items-center justify-center p-6 sm:p-8 md:p-10">
            <div className="w-full max-w-md space-y-6">
              
              {/* Toggle Viewer / Team */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setMode('viewer')}
                  className={`flex-1 py-2 font-semibold rounded-lg transition ${
                    mode === 'viewer'
                      ? 'bg-gradient-to-r from-[#9F00FF] to-[#FB19E2] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Viewer
                </button>
                <button
                  type="button"
                  onClick={() => setMode('team')}
                  className={`flex-1 py-2 font-semibold rounded-lg transition ${
                    mode === 'team'
                      ? 'bg-gradient-to-r from-[#9F00FF] to-[#FB19E2] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Team
                </button>
              </div>

              {/* Sign-in Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  name="email"
                  type="email"
                  placeholder={mode === 'viewer' ? 'Email' : 'Team email'}
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="w-full text-base px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                />

                {/* Password Field */}
                <div className="relative">
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    className="w-full text-base px-4 py-2 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      // Eye-off icon
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
                        viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19
                             c-4.477 0-8.268-2.943-9.542-7
                             a9.956 9.956 0 012.223-3.428M6.18 6.18A9.956 9.956 0 0112 5
                             c4.477 0 8.268 2.943 9.542 7
                             -.162.516-.363 1.02-.597 1.503M3 3l18 18" />
                      </svg>
                    ) : (
                      // Eye icon
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
                        viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5
                             c4.477 0 8.268 2.943 9.542 7
                             -1.274 4.057-5.065 7-9.542 7
                             -4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>

                {/* Submit button with loading */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3 font-semibold rounded-lg text-white transition duration-300 ${
                    isLoading
                      ? 'bg-gradient-to-r from-[#9F00FF]/60 to-[#FB19E2]/60 cursor-not-allowed opacity-75'
                      : 'bg-gradient-to-r from-[#9F00FF] to-[#FB19E2] hover:opacity-90'
                  }`}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 010 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                        ></path>
                      </svg>
                      Signing in...
                    </span>
                  ) : (
                    `Sign in as ${mode === 'viewer' ? 'Viewer' : 'Team'}`
                  )}
                </button>
              </form>

              <p className="text-center text-sm text-gray-600">
                Don’t have an account?{' '}
                <a href="/signup" className="text-[#9F00FF] hover:underline font-medium">
                  Sign Up
                </a>
              </p>
            </div>
          </div>

          {/* RIGHT: Illustration */}
          <div className="hidden md:flex md:w-1/2 bg-purple-100 items-center justify-center p-6 sm:p-8 md:p-10 m-2 rounded-2xl">
            <div className="text-center">
              <img
                src={testimage}
                alt="Dance illustration"
                className="mb-6 w-40 sm:w-48 md:w-56 mx-auto"
              />
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-purple-700 mb-2">
                Back for more dance fun?
              </h2>
              <p className="text-purple-600 text-sm sm:text-base">
                Spice up your groove! Sign in now and let the dance magic continue.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignIn;
